import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calculator, Download, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import {
  hrReportsApi,
  payrollAdjustmentsApi,
  payrollApi,
  payslipsApi,
} from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { PayrollAdjustmentType, PayrollRunResult, Payslip } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { AdjustmentBadge, Days, Money, PayrollStatusBadge, Stat } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';

const ADJUSTMENT_TYPES: { value: PayrollAdjustmentType; label: string }[] = [
  { value: 'OVERTIME', label: 'Overtime' },
  { value: 'BONUS', label: 'Bonus' },
  { value: 'REIMBURSEMENT', label: 'Reimbursement' },
  { value: 'DEDUCTION', label: 'Deduction' },
  { value: 'FINE', label: 'Fine' },
];

/**
 * One payroll run.
 *
 * <p>The three actions follow the lifecycle and appear only where they can
 * succeed: calculate while it is open, approve once it is calculated, mark paid
 * once approved. Approval is the point of no return, so it asks first.
 */
export function PayrollDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('employeeName,asc');

  const [result, setResult] = useState<PayrollRunResult | null>(null);
  const [approving, setApproving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [reference, setReference] = useState('');
  const [addingAdjustment, setAddingAdjustment] = useState(false);

  const period = useQuery({
    queryKey: queryKeys.payroll.detail(id),
    queryFn: () => payrollApi.get(id),
    enabled: id !== '',
  });

  const params = table.params;
  const payslips = useQuery({
    queryKey: queryKeys.payslips.forPeriod(id, params),
    queryFn: () => payslipsApi.forPeriod(id, params),
    enabled: id !== '' && (period.data?.employeeCount ?? 0) > 0,
  });

  const adjustments = useQuery({
    queryKey: queryKeys.payroll.adjustments(id),
    queryFn: () => payrollAdjustmentsApi.list(id),
    enabled: id !== '' && can(PERMISSIONS.PAYROLL_ADJUSTMENT_VIEW),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.payroll.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.payslips.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.hr.dashboard });
  };

  const calculate = useMutation({
    mutationFn: () => payrollApi.calculate(id),
    onSuccess: (run) => {
      setResult(run);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const approve = useMutation({
    mutationFn: () => payrollApi.approve(id),
    onSuccess: () => {
      toast.success('Payroll approved');
      setApproving(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const markPaid = useMutation({
    mutationFn: () => payrollApi.markPaid(id, reference.trim() === '' ? undefined : reference.trim()),
    onSuccess: () => {
      toast.success('Payroll marked paid');
      setPaying(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => payrollApi.cancel(id, reason),
    onSuccess: () => {
      toast.success('Payroll run cancelled');
      invalidate();
      navigate(ROUTES.payroll);
    },
    onError: (error) => handleApiError(error),
  });

  const removeAdjustment = useMutation({
    mutationFn: (adjustmentId: string) => payrollAdjustmentsApi.remove(adjustmentId),
    onSuccess: () => {
      toast.success('Adjustment removed');
      void queryClient.invalidateQueries({ queryKey: queryKeys.payroll.adjustments(id) });
    },
    onError: (error) => handleApiError(error),
  });

  if (period.isError) {
    return <ErrorState error={period.error} onRetry={() => void period.refetch()} />;
  }
  if (period.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const run = period.data;

  const columns: Column<Payslip>[] = [
    {
      key: 'employee',
      header: 'Employee',
      sortKey: 'employeeName',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.employeeName}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.employeeNumber)} · {row.slipNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (row) => <span className="text-[13px] text-muted">{orDash(row.departmentName)}</span>,
    },
    {
      key: 'days',
      header: 'Days',
      className: 'text-right',
      render: (row) => (
        <span className="text-sm">
          <Days value={row.payableDays} />
          <span className="text-subtle">/{row.workingDays}</span>
          {row.unpaidLeaveDays > 0 && (
            <span className="block text-[11px] text-[color:var(--sms-warning)]">
              <Days value={row.unpaidLeaveDays} /> unpaid
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'gross',
      header: 'Gross',
      className: 'text-right',
      render: (row) => <Money value={row.grossEarnings} />,
    },
    {
      key: 'deductions',
      header: 'Deductions',
      className: 'text-right',
      render: (row) => <Money value={row.totalDeductions} />,
    },
    {
      key: 'net',
      header: 'Net',
      sortKey: 'netPay',
      className: 'text-right',
      render: (row) => <Money value={row.netPay} bold />,
    },
  ];

  return (
    <div>
      <Link
        to={ROUTES.payroll}
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All payroll runs
      </Link>

      <PageHeader
        title={run.name}
        description={`${formatDate(run.periodStart)} → ${formatDate(run.periodEnd)} · paid ${formatDate(run.paymentDate)} · ${run.workingDays} working days`}
        action={
          <span className="flex flex-wrap items-center gap-2">
            <PayrollStatusBadge status={run.status} />
            {run.open && can(PERMISSIONS.PAYROLL_CALCULATE) && (
              <Button onClick={() => calculate.mutate()} disabled={calculate.isPending}>
                <Calculator className="h-4 w-4" aria-hidden="true" />
                {calculate.isPending
                  ? 'Calculating…'
                  : run.status === 'DRAFT'
                    ? 'Calculate'
                    : 'Recalculate'}
              </Button>
            )}
            {run.status === 'CALCULATED' && can(PERMISSIONS.PAYROLL_APPROVE) && (
              <Button onClick={() => setApproving(true)}>Approve</Button>
            )}
            {run.status === 'APPROVED' && can(PERMISSIONS.PAYROLL_PAY) && (
              <Button onClick={() => setPaying(true)}>Mark paid</Button>
            )}
            {(run.status === 'APPROVED' || run.status === 'PAID') &&
              can(PERMISSIONS.PAYSLIP_VIEW) && (
                <Button
                  variant="secondary"
                  onClick={() => void hrReportsApi.payrollRegisterCsv(id)}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Transfer list
                </Button>
              )}
            {run.status !== 'PAID' &&
              run.status !== 'CANCELLED' &&
              can(PERMISSIONS.PAYROLL_PERIOD_MANAGE) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    const reason = window.prompt('Why is this run being cancelled?');
                    if (reason !== null && reason.trim() !== '') {
                      cancel.mutate(reason.trim());
                    }
                  }}
                >
                  Cancel run
                </Button>
              )}
          </span>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Payslips" value={run.employeeCount} />
        <Stat label="Gross" value={<Money value={run.totalGross} />} />
        <Stat label="Deductions" value={<Money value={run.totalDeductions} />} />
        <Stat label="Net pay" value={<Money value={run.totalNet} />} tone="success" />
      </div>

      {run.status !== 'DRAFT' && run.status !== 'CANCELLED' && (
        <Card className="mt-3 p-4">
          <dl className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
            {run.calculatedAt !== undefined && (
              <Row label="Calculated" value={formatDate(run.calculatedAt)} />
            )}
            {run.approvedAt !== undefined && (
              <Row
                label="Approved"
                value={`${formatDate(run.approvedAt)} by ${orDash(run.approvedByName)}`}
              />
            )}
            {run.paidAt !== undefined && (
              <Row label="Paid" value={`${formatDate(run.paidAt)} by ${orDash(run.paidByName)}`} />
            )}
          </dl>
        </Card>
      )}

      {can(PERMISSIONS.PAYROLL_ADJUSTMENT_VIEW) && (
        <Card className="mt-3 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-line px-4 py-3">
            <span>
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                One-offs this run
              </h2>
              <p className="text-[12px] text-muted">
                Overtime, bonuses and fines. Entered before calculating; they survive a
                recalculation.
              </p>
            </span>
            {run.open && can(PERMISSIONS.PAYROLL_ADJUSTMENT_MANAGE) && (
              <Button size="sm" variant="secondary" onClick={() => setAddingAdjustment(true)}>
                Add
              </Button>
            )}
          </div>

          {(adjustments.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-muted">
              Nothing extra this month.
            </p>
          ) : (
            <ul>
              {(adjustments.data ?? []).map((adjustment) => (
                <li
                  key={adjustment.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5 last:border-0"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <AdjustmentBadge type={adjustment.adjustmentType} />
                      <span className="text-sm text-ink">{adjustment.label}</span>
                    </span>
                    <span className="mt-0.5 block text-[12px] text-muted">
                      {adjustment.employeeName}
                      {adjustment.hours !== undefined && (
                        <> · {adjustment.hours} hours at {adjustment.hourlyRate}</>
                      )}
                      {adjustment.reason !== undefined && <> · {adjustment.reason}</>}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <Money value={adjustment.amount} bold />
                    {run.open && can(PERMISSIONS.PAYROLL_ADJUSTMENT_MANAGE) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeAdjustment.mutate(adjustment.id)}
                      >
                        Remove
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card className="mt-3 overflow-hidden">
        <div className="border-b-2 border-line px-4 py-3">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Payslips</h2>
        </div>
        <DataTable
          columns={columns}
          rows={payslips.data?.data}
          rowKey={(row) => row.id}
          loading={payslips.isPending && (run.employeeCount ?? 0) > 0}
          error={payslips.error}
          onRetry={() => void payslips.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => navigate(ROUTES.payslip(row.id))}
          emptyTitle="Nothing calculated yet"
          emptyDescription="Calculate the run to produce payslips."
        />
      </Card>

      {/* ---- what the run produced -------------------------------------- */}
      {result !== null && (
        <Modal open onClose={() => setResult(null)} title="Payroll calculated">
          <dl className="text-sm">
            <Row label="Paid" value={`${result.employeesPaid} payslip(s)`} />
            <Row label="Gross" value={<Money value={result.totalGross} />} />
            <Row label="Deductions" value={<Money value={result.totalDeductions} />} />
            <Row label="Net" value={<Money value={result.totalNet} />} />
          </dl>

          {result.skipped.length > 0 && (
            <div className="mt-3 flex items-start gap-2 border border-line bg-surface p-3">
              <TriangleAlert
                className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--sms-warning)]"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-ink">
                  {result.employeesSkipped} not paid
                </p>
                <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
                  {result.skipped.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setResult(null)}>Done</Button>
          </div>
        </Modal>
      )}

      {/* ---- approval ---------------------------------------------------- */}
      {approving && (
        <Modal open onClose={() => setApproving(false)} title="Approve this payroll run?">
          <p className="text-sm text-muted">
            Approving fixes the figures. After this the run cannot be recalculated and no further
            overtime or bonus can be added — the only way back is cancelling the run, which is
            recorded.
          </p>
          <dl className="mt-3 text-sm">
            <Row label="Payslips" value={run.employeeCount} />
            <Row label="Net pay" value={<Money value={run.totalNet} />} />
          </dl>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setApproving(false)}>
              Not yet
            </Button>
            <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </div>
        </Modal>
      )}

      {/* ---- payment ----------------------------------------------------- */}
      {paying && (
        <Modal open onClose={() => setPaying(false)} title="Record the payment">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              markPaid.mutate();
            }}
          >
            <TextField
              label="Bank reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="BANK-TRF-20261005-001"
              hint="Stamped onto every payslip, so each names its own payment"
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setPaying(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={markPaid.isPending}>
                {markPaid.isPending ? 'Saving…' : 'Mark paid'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {addingAdjustment && (
        <AdjustmentDialog
          periodId={id}
          onClose={() => setAddingAdjustment(false)}
          onSaved={() => {
            setAddingAdjustment(false);
            void queryClient.invalidateQueries({ queryKey: queryKeys.payroll.adjustments(id) });
          }}
        />
      )}
    </div>
  );
}

function AdjustmentDialog({
  periodId,
  onClose,
  onSaved,
}: {
  periodId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [type, setType] = useState<PayrollAdjustmentType>('OVERTIME');
  const [label, setLabel] = useState('');
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const byHours = type === 'OVERTIME' && hours !== '' && rate !== '';

  const save = useMutation({
    mutationFn: () =>
      payrollAdjustmentsApi.create({
        periodId,
        employeeId: employee!.id,
        adjustmentType: type,
        label: label.trim(),
        hours: byHours ? Number(hours) : null,
        hourlyRate: byHours ? Number(rate) : null,
        amount: byHours ? null : Number(amount),
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: (adjustment) => {
      toast.success(`${adjustment.label} recorded`);
      onSaved();
    },
    onError: (error) => handleApiError(error),
  });

  return (
    <Modal open onClose={onClose} title="Add a one-off">
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <EmployeePicker value={employee} onChange={setEmployee} />

        <SelectField
          label="Kind"
          required
          value={type}
          onChange={(event) => setType(event.target.value as PayrollAdjustmentType)}
        >
          {ADJUSTMENT_TYPES.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </SelectField>

        <TextField
          label="What it says on the payslip"
          required
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Weekend exam invigilation"
        />

        {type === 'OVERTIME' ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Hours"
                type="number"
                step="0.25"
                min="0"
                value={hours}
                onChange={(event) => setHours(event.target.value)}
              />
              <TextField
                label="Rate per hour"
                type="number"
                step="0.01"
                min="0"
                value={rate}
                onChange={(event) => setRate(event.target.value)}
              />
            </div>
            {byHours ? (
              <p className="text-[13px] text-muted">
                Worked out by the server as{' '}
                <span className="text-ink">
                  <Money value={Number(hours) * Number(rate)} />
                </span>
                . The hours and the rate are the facts; the amount follows from them.
              </p>
            ) : (
              <TextField
                label="Amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                hint="Or give hours and a rate above"
              />
            )}
          </>
        ) : (
          <TextField
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            required
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        )}

        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              employee === null || label.trim() === '' || (!byHours && amount === '') ||
              save.isPending
            }
          >
            {save.isPending ? 'Saving…' : 'Record'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <dt className="text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}
